/**
 * The cave view's geometry: zoom, scrolling, and which cell is under a point (docs/spec/07 §4).
 * A cave smaller than the view is centred in it; a bigger one scrolls. Pure.
 */

import type { Grid, Point } from './grid';

/** Cell size on screen: 16 px at 1x, 8 px at 1/2x. */
export type CellSize = 16 | 8;

export interface View {
  readonly cell: CellSize;
  /** Scroll in screen pixels: how far the cave's left and top edges are past the view's. */
  readonly sx: number;
  readonly sy: number;
}

/** The view's size on screen, in pixels. */
export interface ViewSize {
  readonly w: number;
  readonly h: number;
}

function clampAxis(scroll: number, content: number, room: number): number {
  return content <= room ? 0 : Math.min(content - room, Math.max(0, Math.round(scroll)));
}

export function clampView(v: View, g: Grid, size: ViewSize): View {
  const sx = clampAxis(v.sx, g.w * v.cell, size.w);
  const sy = clampAxis(v.sy, g.h * v.cell, size.h);
  return sx === v.sx && sy === v.sy ? v : { cell: v.cell, sx, sy };
}

/** Where cell (0, 0)'s top-left lands, relative to the view's top-left. */
export function viewOrigin(v: View, g: Grid, size: ViewSize): Point {
  const cw = g.w * v.cell;
  const ch = g.h * v.cell;
  return {
    x: cw <= size.w ? Math.floor((size.w - cw) / 2) : -v.sx,
    y: ch <= size.h ? Math.floor((size.h - ch) / 2) : -v.sy,
  };
}

/** The cell under a point relative to the view's top-left, or null outside the cave. */
export function cellAtPoint(v: View, g: Grid, size: ViewSize, px: number, py: number): Point | null {
  if (px < 0 || py < 0 || px >= size.w || py >= size.h) return null;
  const o = viewOrigin(v, g, size);
  const x = Math.floor((px - o.x) / v.cell);
  const y = Math.floor((py - o.y) / v.cell);
  return x >= 0 && y >= 0 && x < g.w && y < g.h ? { x, y } : null;
}

export function scrollBy(v: View, g: Grid, size: ViewSize, dx: number, dy: number): View {
  return clampView({ cell: v.cell, sx: v.sx + dx, sy: v.sy + dy }, g, size);
}

/** Scroll just enough that `p` sits at least `margin` cells inside the view's edges. */
export function revealCell(v: View, g: Grid, size: ViewSize, p: Point, margin = 2): View {
  const axis = (scroll: number, at: number, room: number): number => {
    const m = Math.min(margin, Math.floor((room / v.cell - 1) / 2));
    const lo = (at - m) * v.cell;
    const hi = (at + m + 1) * v.cell - room;
    return Math.min(lo, Math.max(hi, scroll));
  };
  const next = clampView({ cell: v.cell, sx: axis(v.sx, p.x, size.w), sy: axis(v.sy, p.y, size.h) }, g, size);
  return next.sx === v.sx && next.sy === v.sy ? v : next;
}

/** Change zoom keeping `focus` (a cell) under the same point of the view where possible. */
export function zoomTo(v: View, g: Grid, size: ViewSize, cell: CellSize, focus: Point): View {
  if (cell === v.cell) return v;
  const o = viewOrigin(v, g, size);
  const fx = o.x + (focus.x + 0.5) * v.cell;
  const fy = o.y + (focus.y + 0.5) * v.cell;
  return clampView({ cell, sx: (focus.x + 0.5) * cell - fx, sy: (focus.y + 0.5) * cell - fy }, g, size);
}

/** Visible part of the cave as 0..1 fractions, for scroll bars: [start, end] on each axis. */
export function visibleSpan(v: View, g: Grid, size: ViewSize): { x0: number; x1: number; y0: number; y1: number } {
  const cw = g.w * v.cell;
  const ch = g.h * v.cell;
  return {
    x0: cw <= size.w ? 0 : v.sx / cw,
    x1: cw <= size.w ? 1 : (v.sx + size.w) / cw,
    y0: ch <= size.h ? 0 : v.sy / ch,
    y1: ch <= size.h ? 1 : (v.sy + size.h) / ch,
  };
}
